class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    if (event.target.matches('[data-cart-membership-select]')) {
      return;
    }

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          updateCartMergeTokens();
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        window.Allstora.cart = parsedState;
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

function addCart(id, selling_plan = null) {
  $('.cart-recommended-product[data-id=' + id + '] .loading__spinner').removeClass('hidden');

  let data = {
    id: id,
    quantity: 1,
  };

  if (selling_plan !== null) {
    data.selling_plan = selling_plan;
  }

  jQuery.ajax({
    url: '/cart/add.js',
    type: 'post',
    dataType: 'json',
    data: data,
    success: function (cart) {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    },
  });
}

function membershipFrequencyChange() {
  $(document).on('change', '[data-cart-membership-select]', (e) => {
    e.stopPropagation();

    e.target.closest('.cart-item').style.opacity = 0.5;
    e.target.closest('.cart-item').style.pointerEvents = 'none';

    const removeVariantId = Number(e.target.dataset.cartMembershipSelect);
    const addVariantId = Number(e.target.options[e.target.selectedIndex].value);
    const addSellingPlanId = Number(e.target.options[e.target.selectedIndex].dataset.sellingPlanId);

    jQuery.ajax({
      url: '/cart/change.js',
      type: 'post',
      dataType: 'json',
      data: {
        id: Number(removeVariantId),
        quantity: 0,
      },
      success: (cart) => {
        addCart(addVariantId, addSellingPlanId);
      },
    });
  });
}

membershipFrequencyChange();

function updateCartRecommendation() {
  // Get all <tr> elements inside the cart drawer
  const rows = document.querySelectorAll('tr.cart-item');

  let productId = null;

  // Loop through each <tr> and check for the data-product-id attribute
  for (let i = 0; i < rows.length; i++) {
    productId = rows[i].getAttribute('data-product-id');

    if (productId) {
      console.log('Product ID found in row', i + 1, ':', productId);
      fetchProductRecommendations(productId); // Call the API with the product ID
      break; // Exit the loop once we find a valid product ID
    }
  }

  if (!productId) {
    console.log('No product ID found in any row.');
  }
  const HIDE_RECOMMENDATIONS_TAG = 'hide-from-recommendations';

  // Function to fetch product recommendations
  function fetchProductRecommendations(productId) {
    const apiUrl = `/recommendations/products.json?product_id=${productId}&limit=5`; // Fetch 1 recommendation

    fetch(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        console.log('Product recommendations:', data);
        if (data.products.length > 0) {
          for (const product of data.products) {
            // We need to pick a product that isn't supposed to be hidden from recommendations
            if (!product.tags.includes(HIDE_RECOMMENDATIONS_TAG)) {
              const recommendedProduct = product;
              updateRecommendationRow(recommendedProduct);
            }
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching product recommendations:', error);
      });
  }

  // Function to update the cart recommended product <tr>
  function updateRecommendationRow(product) {
    const recommendationRow = document.querySelector('.cart-item.cart-recommended-product');

    // Extract data from the API response
    const imageUrl = product.featured_image; // Product image URL
    const title = product.title; // Product title
    var price = product.variants[0].price / 100; // Product price (first variant, divided by 100 to get the correct format)
    var compareAtPrice = product.variants[0].compare_at_price / 100; // Original price, if available
    const firstVariantId = product.variants[0].id; // First variant ID
    const productUrl = `/products/${product.handle}`; // Product URL
    const currencySymbol = window.AllstoraCurrency.symbol; // Dynamic currency symbol

    // Update the recommendation row with product details
    if (recommendationRow) {
      // The data-membership-discount is populated by the cart-drawer.liquid
      // It will be either the normal memberhsip discount or the new-member discount where relevant
      if (recommendationRow.hasAttribute('data-membership-discount')) {
        // Get the value of the "data-membership-discount" attribute
        var membershipDiscount = recommendationRow.getAttribute('data-membership-discount');
        console.log(membershipDiscount);

        // We update the prices if a membership discount is provided
        compareAtPrice = price;
        price = compareAtPrice * membershipDiscount;
      }

      recommendationRow.querySelector('.cart-item__image').src = imageUrl; // Update image
      recommendationRow.querySelector('.cart-item__image').alt = title; // Update alt text
      const productNameLink = recommendationRow.querySelector('.cart-item__name'); // Product link
      productNameLink.textContent = title; // Update title text
      productNameLink.href = productUrl; // Update product link

      // Update price with the dynamic currency symbol
      const priceWrapper = recommendationRow.querySelector('.cart-item__price-wrapper');
      if (compareAtPrice && compareAtPrice > price) {
        priceWrapper.innerHTML = `
          <div class="cart-item__discounted-prices">
            <span class="price price--end">${currencySymbol}${price.toFixed(2)}</span>
            <s class="cart-item__old-price price price--end">${currencySymbol}${compareAtPrice.toFixed(2)}</s>
          </div>
        `;
      } else {
        priceWrapper.innerHTML = `<span class="price price--end">${currencySymbol}${price.toFixed(2)}</span>`;
      }

      // Update the Add to Cart button with the variant ID
      const addToCartButton = recommendationRow.querySelector('.button');
      addToCartButton.setAttribute('onclick', `addCart(${firstVariantId})`);

      // Update the data-id attribute with the first variant ID
      recommendationRow.setAttribute('data-id', firstVariantId);

      // Make the hidden row visible once product data is updated
      recommendationRow.style.display = 'grid';
    }
  }
}

function updateCartMergeTokens() {
  const cartDrawerWrapper = document.querySelector('cart-drawer');
  if (cartDrawerWrapper && typeof cartDrawerWrapper.updateMergeTokens === 'function') {
    cartDrawerWrapper.updateMergeTokens();
  }
}

updateCartRecommendation();

// Save the previous cart state
let previousCartState = {};

// Function to check for updates
function checkCartUpdates() {
  fetch('/cart.json')
    .then((response) => response.json())
    .then((cart) => {
      // Compare the new cart state with the previous state
      if (JSON.stringify(cart) !== JSON.stringify(previousCartState)) {
        updateCartRecommendation();
        // Add your action here, e.g., update UI or log event
        updateCartMergeTokens();
      }
      // Update the previous cart state to the current one
      previousCartState = cart;
    })
    .catch((error) => console.error('Error fetching cart:', error));
}

// Poll every 2 seconds (2000 milliseconds)
setInterval(checkCartUpdates, 2000);

// Save the original fetch method
const originalFetch = window.fetch;

window.fetch = function (...args) {
  // Check if the request is targeting the cart API
  if (args[0].includes('/cart/add.js') || args[0].includes('/cart/update.js') || args[0].includes('/cart/change.js')) {
    updateCartRecommendation();
    // Perform any actions here, like updating the UI or tracking events
    updateCartMergeTokens();
  }

  // Call the original fetch method to ensure the request proceeds
  return originalFetch.apply(this, args);
};
